/**
 * Tela de Correio (10.6R): lista de mensagens com recompensas coletáveis.
 * Mostra diamantes, moedas e XP. Segue o padrão ShopScreen.
 */
import { i18n } from "../i18n/i18n";
import { hasMailReward, type MailMessage } from "../progression/Mail";

export interface MailboxData {
  messages: MailMessage[];
  diamonds: number;
  coins: number;
}

export interface MailboxHandlers {
  onClose: () => void;
  onClaim: (id: string) => void;
  onClaimAll: () => void;
  onMarkAllRead: () => void;
  onDeleteAllRead: () => void;
  onRead: (id: string) => void;
  onVip?: () => void;
}

export class MailboxScreen {
  private readonly root: HTMLDivElement;
  private readonly titleEl: HTMLElement;
  private readonly listEl: HTMLElement;
  private readonly diamondsEl: HTMLElement;
  private readonly coinsEl: HTMLElement;
  private readonly emptyEl: HTMLElement;
  private provider: (() => MailboxData) | null = null;

  constructor(uiRoot: HTMLElement, private readonly h: MailboxHandlers) {
    this.root = document.createElement("div");
    this.root.className = "overlay";
    this.root.hidden = true;
    this.root.innerHTML = `
      <div class="panel mailbox-panel">
        <div class="mail-head">
          <h2 class="mail-title"></h2>
          <div class="mail-res">
            <span class="mail-diamonds">💎 <b class="mail-d-n">0</b></span>
            <span class="mail-coins">🪙 <b class="mail-c-n">0</b></span>
          </div>
        </div>
        <div class="mail-empty"></div>
        <div class="mail-list"></div>
        <div class="mail-actions">
          <button class="btn mail-mark-all" data-a="markAll"></button>
          <button class="btn mail-claim-all" data-a="claimAll"></button>
          <button class="btn mail-delete-read" data-a="deleteRead"></button>
        </div>
        <div class="menu-btns">
          <button class="btn" data-a="vip">⭐ VIP</button>
          <button class="btn primary" data-a="close"></button>
        </div>
      </div>`;
    uiRoot.appendChild(this.root);
    this.titleEl = this.root.querySelector(".mail-title")!;
    this.diamondsEl = this.root.querySelector(".mail-d-n")!;
    this.coinsEl = this.root.querySelector(".mail-c-n")!;
    this.listEl = this.root.querySelector(".mail-list")!;
    this.emptyEl = this.root.querySelector(".mail-empty")!;
    const closeBtn = this.root.querySelector<HTMLButtonElement>('[data-a="close"]')!;
    closeBtn.onclick = () => this.h.onClose();
    const claimAllBtn = this.root.querySelector<HTMLButtonElement>('[data-a="claimAll"]')!;
    claimAllBtn.onclick = () => { this.h.onClaimAll(); this.render(); };
    const markAllBtn = this.root.querySelector<HTMLButtonElement>('[data-a="markAll"]')!;
    markAllBtn.onclick = () => { this.h.onMarkAllRead(); this.render(); };
    const delReadBtn = this.root.querySelector<HTMLButtonElement>('[data-a="deleteRead"]')!;
    delReadBtn.onclick = () => { this.h.onDeleteAllRead(); this.render(); };
    const vipBtn = this.root.querySelector<HTMLButtonElement>('[data-a="vip"]');
    if (vipBtn) vipBtn.onclick = () => this.h.onVip?.();
    i18n.onChange(() => { if (!this.root.hidden) this.render(); });
  }

  open(provider: () => MailboxData): void {
    this.provider = provider;
    this.render();
    this.root.hidden = false;
  }

  hide(): void { this.root.hidden = true; }

  refresh(): void { if (!this.root.hidden && this.provider) this.render(); }

  private render(): void {
    const data = this.provider?.() ?? { messages: [], diamonds: 0, coins: 0 };
    this.titleEl.textContent = i18n.t("mail.title");
    this.diamondsEl.textContent = String(data.diamonds);
    this.coinsEl.textContent = String(data.coins);
    this.root.querySelector<HTMLButtonElement>('[data-a="close"]')!.textContent = i18n.t("menu.back");

    this.listEl.innerHTML = "";
    const msgs = data.messages;
    this.emptyEl.textContent = msgs.length === 0 ? i18n.t("mail.empty") : "";
    this.emptyEl.hidden = msgs.length > 0;

    const unclaimed = msgs.filter(m => !m.claimed && hasMailReward(m.rewards)).length;
    const unread = msgs.filter(m => !m.read).length;
    const canDelete = msgs.filter(m => m.read && (!hasMailReward(m.rewards) || m.claimed)).length;
    const claimAllBtn = this.root.querySelector<HTMLButtonElement>('[data-a="claimAll"]')!;
    claimAllBtn.textContent = i18n.t("mail.claimAll");
    claimAllBtn.hidden = unclaimed < 2;
    const markAllBtn = this.root.querySelector<HTMLButtonElement>('[data-a="markAll"]')!;
    markAllBtn.textContent = i18n.t("mail.markAllRead");
    markAllBtn.hidden = unread < 2;
    const delReadBtn = this.root.querySelector<HTMLButtonElement>('[data-a="deleteRead"]')!;
    delReadBtn.textContent = i18n.t("mail.deleteRead");
    delReadBtn.hidden = canDelete < 1;

    for (const m of msgs) {
      const row = document.createElement("div");
      row.className = `mail-item${m.read ? "" : " unread"}${m.claimed ? " claimed" : ""}`;
      const hasReward = hasMailReward(m.rewards);
      const rewardText = this.rewardStr(m);
      row.innerHTML = `
        <div class="mail-icon">${m.claimed ? "📭" : m.read ? "📬" : "📩"}</div>
        <div class="mail-info">
          <div class="mail-name">${this.esc(i18n.t(m.titleKey, m.bodyParams))}</div>
          <div class="mail-body">${this.esc(i18n.t(m.bodyKey, m.bodyParams))}</div>
          ${rewardText ? `<div class="mail-reward">${rewardText}</div>` : ""}
        </div>
        ${hasReward && !m.claimed
          ? `<button class="btn mail-claim">${i18n.t("mail.claim")}</button>`
          : m.claimed ? `<span class="mail-done">${i18n.t("mail.claimed")}</span>` : ""}`;
      const claimBtn = row.querySelector<HTMLButtonElement>(".mail-claim");
      if (claimBtn) claimBtn.onclick = () => { this.h.onClaim(m.id); this.render(); };
      if (!m.read) row.onclick = () => { this.h.onRead(m.id); };
      this.listEl.appendChild(row);
    }
  }

  private rewardStr(m: MailMessage): string {
    const parts: string[] = [];
    if (m.rewards.diamonds) parts.push(`💎 ${m.rewards.diamonds}`);
    if (m.rewards.coins) parts.push(`🪙 ${m.rewards.coins}`);
    if (m.rewards.xp) parts.push(`✨ ${m.rewards.xp} XP`);
    if (m.rewards.elixirs) parts.push(`🧪 ${m.rewards.elixirs} ${i18n.t("mail.elixirsLabel")}`); // 10.9K2 — baú semanal
    if (m.rewards.items?.length) parts.push(`🗡️ ${m.rewards.items.length} ${i18n.t("mail.itemsLabel")}`); // 10.9FM
    return parts.join("  ");
  }

  private esc(s: string): string {
    return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
}
